import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { CreditCard, Lock, Mail, User as UserIcon, Check } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = () => {
  const { cart, getCartTotal, sessionId, loading: cartLoading } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subscribe, setSubscribe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkedCart, setCheckedCart] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'

  useEffect(() => {
    if (!cartLoading && !checkedCart) {
      setCheckedCart(true);
      if (cart.length === 0) {
        navigate('/cart');
      }
    }
  }, [cart, navigate, cartLoading, checkedCart]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout/session`, {
        session_id: sessionId,
        email,
        name: name || undefined,
        subscribe_to_mailing_list: subscribe,
        origin_url: originUrl,
        payment_provider: paymentMethod,
      });

      // Redirect to payment provider
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create checkout session. Please try again.');
      setLoading(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="checkout-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (cart.length === 0 && checkedCart) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold font-display text-foreground mb-2" data-testid="checkout-title">
          Checkout
        </h1>
        <p className="text-muted-foreground mb-8">
          Complete your purchase securely
        </p>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-card border border-border/50 rounded-2xl p-8" data-testid="checkout-form">
              <h2 className="text-xl font-bold font-display text-foreground mb-6 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-primary" />
                Your Information
              </h2>

              {/* Email */}
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-bold text-foreground mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    className="w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                    data-testid="checkout-email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Download links will be sent to this email
                </p>
              </div>

              {/* Name */}
              <div className="mb-6">
                <label htmlFor="name" className="block text-sm font-bold text-foreground mb-2">
                  Name (Optional)
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                    data-testid="checkout-name-input"
                  />
                </div>
              </div>

              {/* Mailing List */}
              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={subscribe}
                    onChange={(e) => setSubscribe(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    data-testid="checkout-subscribe-checkbox"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Join Escola de Samba Casa Samba mailing list to receive updates about new releases and exclusive offers
                  </span>
                </label>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Payment Method
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Stripe Option */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('stripe')}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'stripe'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50 bg-background'
                    }`}
                    data-testid="payment-stripe-btn"
                  >
                    {paymentMethod === 'stripe' && (
                      <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-black" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <CreditCard className="w-6 h-6 text-foreground" />
                      <span className="font-bold text-foreground">Credit Card</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Visa, Mastercard, Amex, Apple Pay, Google Pay
                    </p>
                  </button>

                  {/* PayPal Option */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'paypal'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50 bg-background'
                    }`}
                    data-testid="payment-paypal-btn"
                  >
                    {paymentMethod === 'paypal' && (
                      <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-black" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.769.769 0 0 1 .757-.633h6.837c2.31 0 3.932.478 4.822 1.42.752.795 1.087 1.96.999 3.466-.017.288-.05.591-.1.912l-.002.013v.012c-.32 2.058-1.325 3.492-2.985 4.262-1.192.553-2.67.814-4.52.814h-.607a.769.769 0 0 0-.757.633l-.812 5.14a.641.641 0 0 1-.633.54H7.076v.008z" fill="#002C8A"/>
                        <path d="M19.952 7.26c-.026.166-.055.335-.089.51-.858 4.394-3.795 5.913-7.543 5.913h-1.91a.925.925 0 0 0-.913.782l-.977 6.192-.277 1.756a.487.487 0 0 0 .481.564h3.376a.81.81 0 0 0 .8-.683l.033-.17.636-4.027.04-.22a.81.81 0 0 1 .8-.684h.505c3.265 0 5.822-1.326 6.57-5.16.313-1.602.151-2.94-.676-3.881a3.22 3.22 0 0 0-.856-.892z" fill="#009CDE"/>
                        <path d="M18.855 6.907a7.382 7.382 0 0 0-.911-.202 11.584 11.584 0 0 0-1.84-.134h-5.572a.81.81 0 0 0-.8.683l-1.182 7.5-.034.219a.925.925 0 0 1 .913-.782h1.91c3.748 0 6.685-1.52 7.543-5.913.025-.13.049-.256.07-.378a4.502 4.502 0 0 0-.097-1z" fill="#001F6B"/>
                      </svg>
                      <span className="font-bold text-foreground">PayPal</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pay with your PayPal account
                    </p>
                  </button>
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-stone-900/50 border border-primary/20 rounded-xl p-4 mb-8">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Your payment is secure and encrypted. We never store your card details.
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="checkout-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {paymentMethod === 'paypal' ? (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.769.769 0 0 1 .757-.633h6.837c2.31 0 3.932.478 4.822 1.42.752.795 1.087 1.96.999 3.466-.017.288-.05.591-.1.912l-.002.013v.012c-.32 2.058-1.325 3.492-2.985 4.262-1.192.553-2.67.814-4.52.814h-.607a.769.769 0 0 0-.757.633l-.812 5.14a.641.641 0 0 1-.633.54H7.076v.008z"/>
                      </svg>
                    ) : (
                      <Lock className="w-5 h-5" />
                    )}
                    Pay with {paymentMethod === 'paypal' ? 'PayPal' : 'Card'}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border/50 rounded-2xl p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-lg font-bold font-display text-foreground mb-4">
                Order Summary
              </h2>
              <div className="space-y-3 mb-6">
                {cart.map((item, index) => (
                  <div key={item.id} className="text-sm">
                    <div className="flex justify-between text-foreground">
                      <span className="truncate mr-2">{item.title || `Item ${index + 1}`}</span>
                      <span className="font-bold">${item.price.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-4">
                <div className="flex justify-between text-lg font-bold text-foreground">
                  <span>Total</span>
                  <span className="text-primary font-display" data-testid="checkout-total">
                    ${getCartTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
